import { useFramework } from '@/modules/framework/api';
import { useGenerateControlsWithAnswers } from '@/modules/framework/controls-with-answers';
import { useOwnProducts } from '@/modules/products/api';
import type { Answer } from '@@/packages/shared/types/models/answer';
import { useControlAnswers } from './api/assessment';

export type ControlWithAnswer = { controlID: string; productID?: string; answer?: Answer };

// Responsible for the combining and sorting of controlIDs and answers - we
// always want to show every control, even if there's no answer, but sometimes
// there's more than one answer per control when there's product answers
export function useControlsWithAnswers() {
  const { controlsByDomain, isLoading: isFrameworkLoading } = useFramework();
  const { controlAnswersByControlID, isLoading: isControlAnswersLoading } = useControlAnswers();

  const { ownProducts, isLoading: isProductsLoading } = useOwnProducts();

  const isLoading = computed(
    () => isFrameworkLoading.value || isControlAnswersLoading.value || isProductsLoading.value,
  );

  const productIDs = computed(() => ownProducts.value.map(({ id }) => id));

  // TODO: (30 minutes) we can make several performance improvements by making
  // this a map of controlIDs to arrays of productIDs
  const pendingProductIDs = ref<{ controlID: string; productID: string }[]>([]);

  const { generateControlsWithAnswers } = useGenerateControlsWithAnswers();

  function getControlsWithAnswers(controlIDs: string[] | undefined): ControlWithAnswer[] {
    return generateControlsWithAnswers(
      controlIDs,
      controlAnswersByControlID.value,
      productIDs.value,
      pendingProductIDs.value,
    );
  }

  function controlsWithAnswerForDomain(domainID: string): ControlWithAnswer[] {
    return getControlsWithAnswers(controlsByDomain.value[domainID]);
  }

  function addPendingProduct(controlID: string, productID: string) {
    pendingProductIDs.value.push({
      controlID,
      productID,
    });
  }

  function pendingProductIDsForControl(controlID: string) {
    return pendingProductIDs.value
      .filter(({ controlID: id }) => id === controlID)
      .map(({ productID }) => productID);
  }

  function removePendingProductAnswer(pendingControlID: string, pendingProductID: string) {
    const pendingProductToBeRemoved = pendingProductIDs.value.findIndex(
      ({ controlID, productID }) =>
        pendingControlID === controlID && pendingProductID === productID,
    );
    if (pendingProductToBeRemoved !== -1) {
      pendingProductIDs.value.splice(pendingProductToBeRemoved, 1);
    }
  }

  return {
    isLoading,
    getControlsWithAnswers,
    controlsWithAnswerForDomain,
    addPendingProduct,
    pendingProductIDsForControl,
    removePendingProductAnswer,
    // Wrapped in computed so that it's readonly
    pendingProductIDs: computed(() => pendingProductIDs.value),
  };
}
